import { useEffect } from 'react';
import { ToasterProps } from '@msc-b2c/interfaces/types';
import { Icon } from '../icons/Icon';
import styles from './toaster.module.scss';
import { handleKeyDown } from '@msc-b2c/shared-utils/keyboardUtils';

// Define the icons based on the toast type
const getIconForType = (type: string, iconColor?: string) => {
  switch (type) {
    case 'success':
      return (
        <Icon
          icon={'check'}
          color={iconColor}
        />
      ); // Success icon
    case 'warning':
      return (
        <Icon
          icon={'warning'}
          color={iconColor}
        />
      ); // Warning icon
    case 'error':
      return (
        <Icon
          icon={'error'}
          color={iconColor}
        />
      ); // Error icon
    default:
      return (
        <Icon
          icon={'port'}
          color={iconColor}
        />
      ); // Default icon
  }
};
/**
 * Toaster Component
 *
 * @param ToasterTitle - The title displayed in the header of the toaster.
 * @param ToasterMessage - The message content displayed in the toaster body.
 * @param isAutoClosing - If true, the toaster will automatically close after a specified delay.
 * @param autoClosingDelay - The delay (in milliseconds) before auto-closing the toaster.
 * @param styleOptions - Optional additional class names for custom styling.
 * @param onClose - Function called when the toaster is closed (either manually or automatically).
 * @param closeButton - If true, the toaster will display a close button ('X').
 * @param type - The type of toaster, which affects its style and icon ('success', 'warning', 'error', 'info', 'normal).
 * @param displayIcon - If true, the toaster will display an icon based on the type.
 * @param understoodText - Text for an optional "Understood" action button.
 * @param noThanksText - Text for an optional "No Thanks" action button.
 * @param position - The position where the toaster will appear ('relative', 'top-left', 'top-right', 'bottom-left', 'bottom-right').
 * @param displayActions - If true, the toaster will the actions.
 * @param animation - If false, the toaster won't have the animation
 * @param backgroundColor - The background color of the toaster
 * @param iconColor - The color of the icon
 * @returns JSX.Element
 */

export const Toaster = ({
  ToasterTitle,
  ToasterMessage,
  isAutoClosing,
  autoClosingDelay,
  styleOptions,
  onClose,
  closeButton = true,
  type = 'normal',
  displayIcon,
  understoodText,
  noThanksText,
  position = 'bottom-right',
  displayActions,
  animation = true,
  backgroundColor,
  iconColor,
  onUnderstood,
  onNoThanks,
}: ToasterProps) => {
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isAutoClosing && autoClosingDelay && onClose) {
      timer = setTimeout(() => {
        onClose();
      }, autoClosingDelay);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isAutoClosing, autoClosingDelay, onClose]);

  return (
    <div
      className={`${styles['cmp-toaster']} ${styles[type]} ${styles[position]} ${
        styleOptions ?? ''
      } ${!animation ? styles['no-animation'] : ''}`}
      role="alert"
      aria-live="polite"
      style={{ backgroundColor }}
    >
      {displayIcon && (
        <div className={styles['cmp-toaster__icon']}>{getIconForType(type, iconColor)}</div>
      )}
      <div className={styles['cmp-toaster__content']}>
        <div className={styles['cmp-toaster__header']}>
          {ToasterTitle && (
            <div
              className={styles['cmp-toaster__header--title']}
              style={{ color: iconColor }}
            >
              {ToasterTitle}
            </div>
          )}
        </div>
        <div className={styles['cmp-toaster__body']}>
          {ToasterMessage && (
            <p
              className={styles['cmp-toaster__body--message']}
              style={{ color: iconColor }}
              dangerouslySetInnerHTML={{ __html: ToasterMessage }}
            />
          )}
          {displayActions && (understoodText || noThanksText) && (
            <div className={styles['cmp-toaster__body--actions']}>
              {understoodText && (
                <button
                  className={`${styles['cmp-toaster__body--action']} ${styles['cmp-toaster__body--action--understood']} ${styles[type]}`}
                  onClick={onUnderstood ?? onClose}
                >
                  {understoodText}
                </button>
              )}
              {noThanksText && (
                <button
                  className={`${styles['cmp-toaster__body--action']} ${styles['cmp-toaster__body--action--no-thanks']} ${styles[type]}`}
                  onClick={onClose}
                >
                  {noThanksText}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {closeButton && onClose && (
        <div
          className={`${styles['cmp-toaster__close']} ${styles[type]}`}
          onClick={onClose}
          onKeyDown={(event) => handleKeyDown(event, onClose)}
        >
          <Icon
            icon={'close'}
            color={iconColor}
          />
        </div>
      )}
    </div>
  );
};
